import React, { useState, useEffect } from 'react';
import { useTerms } from '../context/TerminologyContext';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Typography, Grid, Alert, MenuItem, Tooltip, Card,
  CardContent, Stack, Fab, useMediaQuery, useTheme, Chip,
} from '@mui/material';
import {
  Add, Edit, Delete, ContactMail, CloudSync as ZohoIcon,
} from '@mui/icons-material';
import { useConfirmDialog } from './ConfirmDialog';
import {
  getCompanies, createCompany, deleteCompany,
} from '../api';
import CompanyDetail from './CompanyDetail';

const TERMS_OPTIONS = ['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90'];

export default function Companies() {
  const { t } = useTerms();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Detail view state — when set, the list collapses and the detail page renders
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState('overview');
  const [detailInitialEdit, setDetailInitialEdit] = useState(false);

  // Add Client dialog state — Edit moved to the Detail page, so this dialog
  // only handles "Add new"
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [addFormData, setAddFormData] = useState({
    name: '', description: '', default_bill_rate: '', payment_terms: '',
  });

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const res = await getCompanies();
      setCompanies(res.data);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    }
  };

  // ── Detail navigation ──────────────────────────────────────────────

  const openDetail = (companyId, { tab = 'overview', edit = false } = {}) => {
    setSelectedCompanyId(companyId);
    setDetailInitialTab(tab);
    setDetailInitialEdit(edit);
  };

  const closeDetail = () => {
    setSelectedCompanyId(null);
    loadCompanies(); // refresh in case detail saved changes
  };

  const handleDetailUpdate = (updated) => {
    setCompanies(prev => prev.map(c => c.id === updated.id ? updated : c));
  };

  // ── Add Client ────────────────────────────────────────────────────

  const handleOpenAddDialog = () => {
    setAddFormData({ name: '', description: '', default_bill_rate: '', payment_terms: '' });
    setOpenAddDialog(true);
  };

  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async () => {
    try {
      const res = await createCompany(addFormData);
      setSuccess(`${t('client.singular')} created`);
      setOpenAddDialog(false);
      loadCompanies();
      // Open the new company's detail page so the user can immediately add
      // billing details, contacts, etc.
      openDetail(res.data.id, { tab: 'overview', edit: false });
    } catch (err) {
      setError(err.response?.data?.error || `Failed to create ${t('client.singular', { lower: true })}`);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: `Delete ${t('client.singular')}`,
      message: `Are you sure you want to delete this ${t('client.singular', { lower: true })}? This action cannot be undone.`,
      confirmText: 'Delete', confirmColor: 'error', severity: 'warning',
    });
    if (!confirmed) return;
    try {
      await deleteCompany(id);
      setSuccess(`${t('client.singular')} deleted`);
      loadCompanies();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to delete ${t('client.singular', { lower: true })}`);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  // When a company is selected, the entire view becomes the Detail page.
  // The list state is preserved underneath so coming back is instant.
  if (selectedCompanyId) {
    const selected = companies.find(c => c.id === selectedCompanyId);
    if (!selected) {
      // Company was deleted or list reloaded without it — bounce back to list.
      setSelectedCompanyId(null);
      return null;
    }
    return (
      <Box sx={{ p: isMobile ? 1 : 2 }}>
        <CompanyDetail
          company={selected}
          onBack={closeDetail}
          onUpdate={handleDetailUpdate}
          initialTab={detailInitialTab}
          initialEditMode={detailInitialEdit}
        />
        <ConfirmDialogComponent />
      </Box>
    );
  }

  // Mobile card list
  const renderMobile = () => (
    <Stack spacing={1.5}>
      {companies.map(c => (
        <Card key={c.id} variant="outlined">
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="subtitle1" fontWeight="medium"
                    sx={{ cursor: 'pointer', color: 'primary.main' }}
                    onClick={() => openDetail(c.id)}
                  >
                    {c.name}
                  </Typography>
                  {c.zoho_contact_id && (
                    <Tooltip title="Linked to Zoho">
                      <Chip icon={<ZohoIcon fontSize="small" />} label="Zoho" size="small" variant="outlined" color="primary" />
                    </Tooltip>
                  )}
                </Stack>
                {c.description && (
                  <Typography variant="body2" color="text.secondary">
                    {c.description}
                  </Typography>
                )}
                {c.payment_terms && (
                  <Typography variant="caption" color="text.secondary">
                    Terms: {c.payment_terms}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Open contacts tab">
                  <IconButton size="small" onClick={() => openDetail(c.id, { tab: 'contacts' })}>
                    <ContactMail fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Edit ${t('client.singular', { lower: true })}`}>
                  <IconButton size="small" onClick={() => openDetail(c.id, { edit: true })}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Delete ${t('client.singular', { lower: true })}`}>
                  <IconButton size="small" onClick={() => handleDelete(c.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
      {companies.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No {t('client.plural', { lower: true })} yet</Typography>
        </Box>
      )}
    </Stack>
  );

  // Desktop table list
  const renderDesktop = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payment Terms</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Default Bill Rate</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.map(c => (
            <TableRow key={c.id} hover>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500 }}
                    onClick={() => openDetail(c.id)}
                  >
                    {c.name}
                  </Typography>
                  {c.zoho_contact_id && (
                    <Tooltip title={`Linked to Zoho contact ${c.zoho_contact_id}`}>
                      <Chip
                        icon={<ZohoIcon fontSize="small" />}
                        label="Zoho" size="small" variant="outlined" color="primary"
                      />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell>{c.description || <em style={{ opacity: 0.5 }}>—</em>}</TableCell>
              <TableCell>{c.payment_terms || <em style={{ opacity: 0.5 }}>(tenant default)</em>}</TableCell>
              <TableCell>
                {c.default_bill_rate != null
                  ? `$${Number(c.default_bill_rate).toFixed(2)}/hr`
                  : <em style={{ opacity: 0.5 }}>—</em>}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Open contacts tab">
                  <IconButton size="small" onClick={() => openDetail(c.id, { tab: 'contacts' })} aria-label="View contacts">
                    <ContactMail fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Edit ${t('client.singular', { lower: true })}`}>
                  <IconButton size="small" onClick={() => openDetail(c.id, { edit: true })} aria-label={`Edit ${t('client.singular', { lower: true })}`}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Delete ${t('client.singular', { lower: true })}`}>
                  <IconButton size="small" onClick={() => handleDelete(c.id)} aria-label={`Delete ${t('client.singular', { lower: true })}`}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {companies.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography color="text.secondary" sx={{ py: 4 }}>No {t('client.plural', { lower: true })} yet</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('client.plural')}</Typography>
        {!isMobile && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAddDialog}>
            {`Add ${t('client.singular')}`}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {isMobile ? renderMobile() : renderDesktop()}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label={`Add ${t('client.singular', { lower: true })}`}
          sx={{ position: 'fixed', bottom: 80, right: 16 }}
          onClick={handleOpenAddDialog}
        >
          <Add />
        </Fab>
      )}

      {/* Add Client dialog — small, just enough to create the row.
          Fuller editing lives on the Detail page, which we open right after save. */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm" fullWidth fullScreen={isMobile}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleAddSubmit();
          }
        }}
      >
        <DialogTitle>{`Add New ${t('client.singular')}`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth required size="small" label={`${t('client.singular')} Name`}
                name="name" value={addFormData.name} onChange={handleAddInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} size="small" label="Description"
                name="description" value={addFormData.description} onChange={handleAddInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" size="small" label="Default Bill Rate"
                name="default_bill_rate" value={addFormData.default_bill_rate} onChange={handleAddInputChange}
                InputProps={{ startAdornment: '$' }}
                inputProps={{ step: '0.01', min: 0 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth select size="small" label="Default Payment Terms"
                name="payment_terms" value={addFormData.payment_terms} onChange={handleAddInputChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value=""><em>(Use tenant default)</em></MenuItem>
                {TERMS_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={!addFormData.name}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
